import { useState } from 'react'
import classNames from 'classnames'
import Compressor from 'compressorjs'
import { useFormik } from 'formik'

import FormControl from '@/components/base/FormControl'
import Submit from '@/components/base/Submit'
import Typography from '@/components/base/Typography'
import { useLocaleStore } from '@/store/locale'
import {
  convertJsontoFormData,
  createInitialValues,
  createYupSchema
} from '@/utils/form'
import { API } from '@/constants'
import { axiosServer as axios } from '@/base/axios'

import { useStore } from '@/contexts/store'
import { WPCF7Form } from '@/types'

export type FromProps = Partial<{
  form_id: string
  fields: WPCF7Form['form']
}>

const Form = ({ form_id, fields = [] }: FromProps) => {
  const currentLocale = useLocaleStore((state) => state.currentLocale)
  const [statusMessage, setStatusMessage] = useState('')
  const { strings } = useStore()

  /*------------------------------
  Handle Send Form
  ------------------------------*/
  async function sendContactForm(sendData: any) {
    const headers = {
      'Content-Type': 'multipart/form-data'
    }
    const response = await axios.post(
      `${API.GRAVITY_FORM}/${form_id}/submissions`,
      convertJsontoFormData(sendData),
      { headers }
    )
    return response
  }

  const formik = useFormik<any>({
    initialValues: createInitialValues(fields) || {},
    validationSchema: createYupSchema(fields, currentLocale),
    enableReinitialize: true,
    onSubmit: (values, { setStatus, setSubmitting, resetForm }) => {
      const form_url_id = fields.find((field) => field.label === 'form_url')?.id
      const result = sendContactForm({
        ...values,
        ...(form_url_id
          ? { [`input_${form_url_id}`]: window.location.href }
          : {})
      })
      result
        .then((response) => {
          setStatusMessage(response.data.confirmation_message)
          setStatus(response.data.is_valid ? 'success' : 'error')
        })
        .then(() => {
          setTimeout(() => {
            resetForm()
            setStatusMessage('')
            setStatus('')

            setSubmitting(false)
          }, 2000)
        })
    }
  })

  return (
    <form
      noValidate
      autoComplete="off"
      onSubmit={formik.handleSubmit}
      className="grid grid-cols-2 gap-x-gutter gap-y-sm"
      data-motion-form
    >
      {fields.map((field, index) => (
        <FormControl
          key={index}
          id={`input_${field.id}`}
          name={`input_${field.id}`}
          type={field.type}
          label={field.type === 'consent' ? field.checkboxLabel : field.label}
          required={field.isRequired}
          options={field.choices}
          placeholder={`${field.placeholder}${field.isRequired ? ' *' : ''}`}
          checked={formik.values[`input_${field.id}`] || false}
          value={
            field.type === 'fileupload'
              ? undefined
              : formik.values[`input_${field.id}`]
          }
          disabled={formik.isSubmitting}
          onChange={(e) => {
            formik.handleChange(e)
            if (field.type === 'consent') {
              formik.setFieldValue(
                `input_${field.id}_1`,
                e.target.checked ? '1' : '0',
                false
              )
            }
            if (field.type === 'fileupload') {
              const file = e.target.files[0]
              if (file.type.includes('image')) {
                new Compressor(file, {
                  quality: 0.3,
                  success: (res) => {
                    const image = new File([res], (res as File).name)
                    formik.setFieldValue(`input_${field.id}`, image)
                  }
                })
              } else {
                formik.setFieldValue(`input_${field.id}`, file)
              }
            }
          }}
          onBlur={formik.handleBlur}
          error={
            formik.errors[`input_${field.id}`] !== undefined &&
            formik.touched[`input_${field.id}`] !== undefined
          }
          className={classNames('col-span-2', {
            'lg:col-span-2': field.cssClass !== 'half',
            'lg:col-span-1': field.cssClass === 'half'
          })}
        />
      ))}

      <div className="col-span-2">
        <Submit
          variant="contained"
          className="w-full justify-center"
          fetching={formik.isSubmitting}
          disabled={
            formik.isSubmitting ||
            (Object.values(formik.errors).length > 0 && formik.submitCount > 0)
          }
        >
          {strings?.labels?.submit}
        </Submit>
      </div>
      <div className="col-span-2">
        <Typography
          paragraph
          variant="body"
          className={classNames('text-blue-soft', {
            invisible: statusMessage === '',
            visible: statusMessage !== ''
          })}
        >
          {statusMessage}
        </Typography>
      </div>
    </form>
  )
}

export default Form
